// Pipeline-specific types for client onboarding visualization
import type { Client } from './index'

export interface OnboardingClient {
  id: string
  firstName: string
  lastName: string
  email: string
  phone?: string
  tier: 'Basic' | 'Standard' | 'Premium' | 'Elite'
  stage: 'leads' | 'consultation' | 'contract' | 'payment' | 'onboarding' | 'active' | 'stalled'
  joinDate: string
  progress: number
  assignedCoach?: string
  notes: string
  priority: 'low' | 'medium' | 'high'
  value: number
  daysInStage: number
  completedSteps: string[]
  originalClient: Client // Reference to original client data
}

export interface PipelineStage {
  key: string
  title: string
  description: string
  color: string
  icon: string
  clients: OnboardingClient[]
}

export interface PipelineFilters {
  searchText: string
  stages: string[]
  priorities: string[]
  tiers: string[]
  coaches: string[]
  daysInStageRange: [number, number] | null
  valueRange: [number, number] | null
}

export interface PipelineStats {
  totalClients: number
  totalValue: number
  avgDaysInStage: number
  conversionRate: number
  byStage: Record<string, number>
  byPriority: Record<string, number>
}

// Onboarding steps for progress tracking
export const onboardingSteps = [
  'Initial Contact',
  'Consultation Scheduled', 
  'Assessment Completed',
  'Program Designed',
  'Contract Sent',
  'Payment Processed',
  'Coach Assigned',
  'First Session Scheduled'
]

// Convert Client to OnboardingClient for pipeline visualization
export const mapClientToOnboardingClient = (client: Client): OnboardingClient => {
  const daysCreated = Math.floor((Date.now() - new Date(client.assignedDate).getTime()) / (1000 * 60 * 60 * 24))
  
  // Map status to pipeline stage
  let stage: OnboardingClient['stage'] = 'leads'
  let progress = 0
  
  switch (client.status) {
    case 'onboarding':
      stage = 'onboarding'
      progress = Math.min(75 + Math.random() * 20, 95) // 75-95%
      break
    case 'active':
      stage = 'active'
      progress = 100
      break
    case 'inactive':
      stage = 'stalled'
      progress = Math.random() * 60 // 0-60%
      break
    case 'paused':
      stage = 'stalled'
      progress = Math.random() * 80 // 0-80%
      break
    default:
      stage = 'leads'
      progress = Math.random() * 25 // 0-25%
  }
  
  // Determine tier based on program complexity or random assignment
  const tiers: OnboardingClient['tier'][] = ['Basic', 'Standard', 'Premium', 'Elite']
  const tier = tiers[Math.floor(Math.random() * tiers.length)]
  
  // Determine priority based on tier and engagement
  let priority: OnboardingClient['priority'] = 'medium'
  if (tier === 'Elite' || tier === 'Premium') {
    priority = 'high'
  } else if (tier === 'Basic') {
    priority = 'low'
  }
  
  // Simulate value based on tier and program
  const baseValue = {
    'Basic': 150,
    'Standard': 250,
    'Premium': 400,
    'Elite': 600
  }[tier]
  
  const value = baseValue + Math.floor(Math.random() * 100)
  
  // Extract names
  const nameParts = client.name.split(' ')
  const firstName = nameParts[0] || ''
  const lastName = nameParts.slice(1).join(' ') || ''
  
  return {
    id: client.id,
    firstName,
    lastName,
    email: client.email,
    phone: client.phoneNumber,
    tier,
    stage,
    joinDate: client.assignedDate.toISOString().split('T')[0],
    progress: Math.round(progress),
    assignedCoach: client.coachId, // You might want to resolve this to actual coach name
    notes: client.currentProgram?.description || `${tier} tier client with ${client.goals.length} active goals`,
    priority,
    value,
    daysInStage: daysCreated,
    completedSteps: getCompletedSteps(Math.round(progress)),
    originalClient: client
  }
}

// Get completed steps based on progress percentage
export const getCompletedSteps = (progress: number): string[] => {
  const steps = []
  if (progress >= 12.5) steps.push('Initial Contact')
  if (progress >= 25) steps.push('Consultation Scheduled')
  if (progress >= 37.5) steps.push('Assessment Completed')
  if (progress >= 50) steps.push('Program Designed')
  if (progress >= 62.5) steps.push('Contract Sent')
  if (progress >= 75) steps.push('Payment Processed')
  if (progress >= 87.5) steps.push('Coach Assigned')
  if (progress >= 100) steps.push('First Session Scheduled')
  return steps
}

// Pipeline stage configuration
export const pipelineStages: Omit<PipelineStage, 'clients'>[] = [
  {
    key: 'leads',
    title: 'New Leads',
    description: 'Initial inquiries and prospects',
    color: 'bg-blue-500',
    icon: '👤'
  },
  {
    key: 'consultation',
    title: 'Consultation',
    description: 'Consultation scheduled or completed',
    color: 'bg-purple-500', 
    icon: '📋'
  },
  {
    key: 'contract',
    title: 'Contract Pending',
    description: 'Contract sent, awaiting signature',
    color: 'bg-orange-500',
    icon: '📝'
  },
  {
    key: 'payment',
    title: 'Payment Pending', 
    description: 'Contract signed, processing payment',
    color: 'bg-yellow-500',
    icon: '💳'
  },
  {
    key: 'onboarding',
    title: 'Onboarding',
    description: 'Setting up program and coach assignment',
    color: 'bg-indigo-500',
    icon: '🏃‍♀️'
  },
  {
    key: 'active',
    title: 'Active Clients',
    description: 'Successfully onboarded and training',
    color: 'bg-green-500',
    icon: '✅'
  },
  {
    key: 'stalled',
    title: 'Stalled/Issues',
    description: 'Requires attention or follow-up',
    color: 'bg-red-500',
    icon: '⚠️'
  }
]